import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Topic = "orders" | "offers" | "autoship" | "cart";
type SegmentType = "all" | "favorited_product" | "abandoned_cart" | "inactive_buyers";

interface Campaign {
  id: string;
  title: string;
  body: string;
  url: string;
  topic: Topic;
  segment_type: SegmentType;
  segment_params: Record<string, any>;
  status: string;
  target_count: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  sent_at: string | null;
  error: string | null;
}

const TOPICS: Topic[] = ["offers", "orders", "autoship", "cart"];
const SEGMENTS: { id: SegmentType; label: string }[] = [
  { id: "all", label: "Todos los suscriptores" },
  { id: "favorited_product", label: "Favoritearon un producto" },
  { id: "abandoned_cart", label: "Carrito abandonado" },
  { id: "inactive_buyers", label: "Compradores inactivos" },
];

export function PushCampaignsManager({ lang = "es" as "es" | "en" }: { lang?: "es" | "en" }) {
  const [list, setList] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testingUser, setTestingUser] = useState(false);
  const [testUserId, setTestUserId] = useState("");

  const [form, setForm] = useState({
    title: "",
    body: "",
    url: "/",
    topic: "offers" as Topic,
    segment_type: "all" as SegmentType,
    product_id: "",
    hours: 4,
    days: 30,
  });

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("push_campaigns")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    setList((data as any) || []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function segmentParams(): Record<string, any> {
    if (form.segment_type === "favorited_product") return { product_id: form.product_id.trim() };
    if (form.segment_type === "abandoned_cart") return { hours: Number(form.hours) || 4 };
    if (form.segment_type === "inactive_buyers") return { days: Number(form.days) || 30 };
    return {};
  }

  async function createDraft() {
    if (!form.title.trim()) { setMsg("El título es obligatorio"); return; }
    setSaving(true); setMsg(null);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("push_campaigns").insert({
      title: form.title.trim(),
      body: form.body.trim(),
      url: form.url.trim() || "/",
      topic: form.topic,
      segment_type: form.segment_type,
      segment_params: segmentParams(),
      status: "draft",
      created_by: user?.id ?? null,
    });
    setSaving(false);
    if (error) { setMsg("Error: " + error.message); return; }
    setMsg("Borrador guardado");
    setForm({ ...form, title: "", body: "" });
    load();
  }

  async function sendTestToMe() {
    if (!form.title.trim()) { setMsg("Pon un título primero"); return; }
    setTesting(true); setMsg(null);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setTesting(false); setMsg("Inicia sesión"); return; }
    const { data, error } = await supabase.functions.invoke("send-push", {
      body: {
        user_ids: [user.id],
        topic: form.topic,
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url.trim() || "/",
        tag: `test-${Date.now()}`,
      },
    });
    setTesting(false);
    if (error) { setMsg("Error: " + error.message); return; }
    const total = Number(data?.total ?? 0);
    const sent = Number(data?.sent ?? 0);
    if (total === 0) {
      setMsg("No tienes suscripción push activa. Actívala en Ajustes → Notificaciones e intenta de nuevo.");
    } else {
      setMsg(`Prueba enviada: ok=${sent}/${total} fail=${data?.failed ?? 0} (revisa tu dispositivo)`);
    }
  }

  async function sendTestToUser() {
    const uid = testUserId.trim();
    if (!uid) { setMsg("Pega un user_id"); return; }
    if (!form.title.trim()) { setMsg("Pon un título primero"); return; }
    setTestingUser(true); setMsg(null);
    const { data, error } = await supabase.functions.invoke("send-push", {
      body: {
        user_ids: [uid],
        topic: form.topic,
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url.trim() || "/",
        tag: `test-user-${Date.now()}`,
      },
    });
    setTestingUser(false);
    if (error) { setMsg("Error: " + error.message); return; }
    const total = Number(data?.total ?? 0);
    const sent = Number(data?.sent ?? 0);
    if (total === 0) {
      setMsg(`El user_id no tiene suscripción push activa (total=0).`);
    } else {
      setMsg(`Prueba enviada a usuario: ok=${sent}/${total} fail=${data?.failed ?? 0}`);
    }
  }

  async function send(c: Campaign) {
    if (!confirm(`Enviar campaña "${c.title}" a segmento ${c.segment_type}?`)) return;
    setSendingId(c.id); setMsg(null);
    const { data, error } = await supabase.functions.invoke("admin-send-campaign", {
      body: { campaign_id: c.id },
    });
    setSendingId(null);
    if (error) { setMsg("Error: " + error.message); load(); return; }
    setMsg(`Enviado: target=${data?.target ?? 0} ok=${data?.sent ?? 0} fail=${data?.failed ?? 0}`);
    load();
  }

  async function remove(id: string) {
    if (!confirm("¿Borrar esta campaña?")) return;
    await supabase.from("push_campaigns").delete().eq("id", id);
    load();
  }

  const card: React.CSSProperties = {
    background: "var(--bg2)", border: "1px solid var(--bd2)",
    borderRadius: 12, padding: 16, marginBottom: 12,
  };
  const input: React.CSSProperties = {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1px solid var(--bd2)", background: "var(--bg)", color: "var(--tx)",
    fontSize: 14, marginTop: 4,
  };
  const label: React.CSSProperties = { display: "block", fontSize: 12, color: "var(--tx3)", marginTop: 10 };
  const btn = (variant: "primary" | "ghost" | "danger" = "primary"): React.CSSProperties => ({
    padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
    border: "1px solid var(--bd2)",
    background:
      variant === "primary" ? "var(--ac)"
      : variant === "danger" ? "transparent"
      : "var(--bg)",
    color:
      variant === "primary" ? "white"
      : variant === "danger" ? "#e74c3c"
      : "var(--tx)",
  });

  return (
    <div style={{ padding: 4 }}>
      <h3 style={{ margin: "8px 0 12px" }}>📢 Campañas push</h3>

      <div style={card}>
        <strong style={{ fontSize: 14 }}>Nueva campaña</strong>

        <label style={label}>Título *
          <input style={input} value={form.title}
            maxLength={60}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="🔥 Oferta exclusiva esta semana" />
        </label>

        <label style={label}>Mensaje
          <input style={input} value={form.body}
            maxLength={140}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="20% off en proteínas premium hasta el domingo." />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={label}>URL de destino
            <input style={input} value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="/products" />
          </label>
          <label style={label}>Tópico
            <select style={input} value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value as Topic })}>
              {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>

        <label style={label}>Segmento
          <select style={input} value={form.segment_type}
            onChange={(e) => setForm({ ...form, segment_type: e.target.value as SegmentType })}>
            {SEGMENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>

        {form.segment_type === "favorited_product" && (
          <label style={label}>Product ID
            <input style={input} value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              placeholder="uuid del producto" />
          </label>
        )}
        {form.segment_type === "abandoned_cart" && (
          <label style={label}>Horas mínimas de abandono
            <input style={input} type="number" min={1} value={form.hours}
              onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} />
          </label>
        )}
        {form.segment_type === "inactive_buyers" && (
          <label style={label}>Días sin compra
            <input style={input} type="number" min={1} value={form.days}
              onChange={(e) => setForm({ ...form, days: Number(e.target.value) })} />
          </label>
        )}

        <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button style={btn("primary")} onClick={createDraft} disabled={saving}>
            {saving ? "Guardando…" : "Guardar borrador"}
          </button>
          <button style={btn("ghost")} onClick={sendTestToMe} disabled={testing}>
            {testing ? "Enviando…" : "Enviar prueba a mí"}
          </button>
          {msg && <span style={{ fontSize: 12, color: "var(--tx2)" }}>{msg}</span>}
        </div>

        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed var(--bd2)" }}>
          <label style={label}>Enviar prueba a un user_id específico
            <input style={input} value={testUserId}
              onChange={(e) => setTestUserId(e.target.value)}
              placeholder="uuid del usuario (debe tener push activado)" />
          </label>
          <div style={{ marginTop: 8 }}>
            <button style={btn("ghost")} onClick={sendTestToUser} disabled={testingUser}>
              {testingUser ? "Enviando…" : "Enviar prueba a este usuario"}
            </button>
          </div>
        </div>
      </div>

      <strong style={{ fontSize: 14 }}>Historial</strong>
      {loading ? <p style={{ color: "var(--tx3)" }}>Cargando…</p> :
        list.length === 0 ? <p style={{ color: "var(--tx3)" }}>Sin campañas todavía.</p> :
        list.map((c) => (
          <div key={c.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{c.title}</div>
                <div style={{ fontSize: 12, color: "var(--tx2)", marginTop: 2 }}>{c.body}</div>
                <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 6 }}>
                  {c.segment_type} · {c.topic} · {new Date(c.created_at).toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 2 }}>
                  Estado: <b>{c.status}</b> · target {c.target_count} · ok {c.sent_count} · fail {c.failed_count}
                  {c.error ? ` · ${c.error}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {c.status === "draft" || c.status === "failed" ? (
                  <button style={btn("primary")} onClick={() => send(c)} disabled={sendingId === c.id}>
                    {sendingId === c.id ? "Enviando…" : "Enviar"}
                  </button>
                ) : null}
                <button style={btn("danger")} onClick={() => remove(c.id)}>Borrar</button>
              </div>
            </div>
          </div>
        ))
      }
    </div>
  );
}

export default PushCampaignsManager;